import { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Eye, Download, FileText, Image, FileWarning } from 'lucide-react';
import { documentsApi } from '@/api/endpoints/documents.api';
import type { DocumentDetail } from '@/types/document.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface DocumentDetailDialogProps {
  documentId: string;
  onClose: () => void;
}

export default function DocumentDetailDialog({
  documentId,
  onClose,
}: DocumentDetailDialogProps) {
  const [document, setDocument] = useState<DocumentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // File blob state for authenticated viewing
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl]);

  const fetchDocument = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const doc = await documentsApi.getById(documentId);
      setDocument(doc);
    } catch (err) {
      console.error('Erro ao carregar documento:', err);
      setError('Erro ao carregar documento');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  const fetchFile = useCallback(async () => {
    setFileLoading(true);
    setFileError(null);
    try {
      const blob = await documentsApi.download(documentId);
      const url = URL.createObjectURL(blob);
      setFileUrl(url);
    } catch (err) {
      console.error('Erro ao carregar arquivo:', err);
      setFileError('Arquivo não disponível para visualização');
    } finally {
      setFileLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    fetchDocument();
    fetchFile();
  }, [fetchDocument, fetchFile]);

  const handleDownload = async () => {
    if (!document) return;
    try {
      const blob = await documentsApi.download(documentId);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.originalFileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar documento:', err);
      setFileError('Erro ao baixar documento');
    }
  };

  const handleOpenInNewTab = () => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const renderFileViewer = () => {
    if (fileLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">Carregando arquivo...</p>
          </div>
        </div>
      );
    }

    if (fileError || !fileUrl) {
      return (
        <div className="flex h-full items-center justify-center text-muted-foreground">
          <div className="text-center">
            <FileWarning className="mx-auto h-16 w-16 text-yellow-500" />
            <p className="mt-4 font-medium">Arquivo não disponível</p>
            <p className="mt-2 text-sm">
              {fileError || 'O arquivo não existe no servidor.'}
            </p>
          </div>
        </div>
      );
    }

    if (document?.isPdf) {
      return (
        <iframe
          src={fileUrl}
          className="h-full w-full"
          title={document.title || document.originalFileName}
        />
      );
    }

    if (document?.isImage) {
      return (
        <div className="flex h-full items-center justify-center p-4">
          <img
            src={fileUrl}
            alt={document.title || document.originalFileName}
            className="max-h-full max-w-full object-contain"
          />
        </div>
      );
    }

    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <FileText className="mx-auto h-16 w-16" />
          <p className="mt-4">Visualização não disponível para este tipo de arquivo</p>
          <button
            onClick={handleDownload}
            className="mt-4 rounded-lg bg-primary px-4 py-2 text-primary-foreground"
          >
            Baixar Arquivo
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="flex flex-col items-center gap-4 rounded-lg bg-background p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p>Carregando documento...</p>
        </div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="rounded-lg bg-background p-8">
          <p className="text-red-600">{error || 'Documento não encontrado'}</p>
          <button onClick={onClose} className="mt-4 rounded-lg bg-primary px-4 py-2 text-primary-foreground">
            Fechar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex bg-black/50">
      <div className="m-4 flex flex-1 flex-col overflow-hidden rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4">
          <div className="flex items-center gap-3">
            {document.isPdf ? (
              <FileText className="h-5 w-5 text-red-500" />
            ) : document.isImage ? (
              <Image className="h-5 w-5 text-blue-500" />
            ) : (
              <FileText className="h-5 w-5 text-gray-500" />
            )}
            <div>
              <h3 className="font-semibold">{document.title || document.originalFileName}</h3>
              <p className="text-sm text-muted-foreground">
                {document.documentTypeLabel} • {formatDate(document.documentDate)} • {document.patientName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              disabled={!!fileError}
              className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Baixar
            </button>
            <button
              onClick={handleOpenInNewTab}
              disabled={!fileUrl}
              className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              <Eye className="h-4 w-4" />
              Abrir em Nova Aba
            </button>
            <button onClick={onClose} className="rounded p-1 hover:bg-muted">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Document Viewer */}
        <div className="flex-1 overflow-hidden bg-muted/50">
          {renderFileViewer()}
        </div>
      </div>
    </div>
  );
}
